import ExcelJS from 'exceljs'
import dayjs from 'dayjs'
import {
  EXCEL_TEXT_NUMFMT,
  excelDateTextValidationFormula,
} from 'helpers/excel'

/* -------------------------------------------------------------------------- */
/*                               Fill presets                                 */
/* -------------------------------------------------------------------------- */

/** Dark navy header fill — use on title rows and column header rows. */
export const XLSX_HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF1F3864' },
}

/** Blue header fill for editable columns (e.g. CSV update templates). */
export const XLSX_EDITABLE_HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF2E75B6' },
}

/** Light blue-grey section fill — use on section label rows. */
export const XLSX_SECTION_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFE8EDF5' },
}

const LOCKED_DATA_FILL = { even: 'FFD6DCE4', odd: 'FFE9EDF2' } as const
const EDITABLE_DATA_FILL = { even: 'FFFFFFFF', odd: 'FFF5F8FC' } as const

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/** 1-based worksheet column index for a field after locked prefix columns. */
export function xlsxDataColumnIndex(
  lockedColumnCount: number,
  fieldIndex: number
): number {
  return fieldIndex + lockedColumnCount + 1
}

/* -------------------------------------------------------------------------- */
/*                           Worksheet primitives                             */
/* -------------------------------------------------------------------------- */

/**
 * Write a full-width section label (spans columns 1–2) to the given row.
 * Intended for use in metadata/summary sheets.
 */
export function xlsxAddSectionLabel(
  ws: ExcelJS.Worksheet,
  label: string,
  rowNum: number
): void {
  const row = ws.getRow(rowNum)
  const cell = row.getCell(1)
  cell.value = label
  cell.font = { bold: true, size: 10, color: { argb: 'FF1F3864' } }
  cell.fill = XLSX_SECTION_FILL
  cell.alignment = { vertical: 'middle' }
  ws.mergeCells(rowNum, 1, rowNum, 2)
  row.height = 20
}

/**
 * Write a key-value pair row (column 1 = key, column 2 = value).
 * Intended for use in metadata/summary sheets.
 */
export function xlsxAddKvRow(
  ws: ExcelJS.Worksheet,
  key: string,
  value: string,
  rowNum: number
): void {
  const row = ws.getRow(rowNum)

  const kCell = row.getCell(1)
  kCell.value = key
  kCell.font = { size: 10, color: { argb: 'FF555555' } }
  kCell.alignment = { vertical: 'middle' }

  const vCell = row.getCell(2)
  vCell.value = value
  vCell.font = { size: 10 }
  vCell.alignment = { vertical: 'middle', wrapText: true }
  row.height = 18
}

/* -------------------------------------------------------------------------- */
/*                    Editable table styling (locked + fields)                  */
/* -------------------------------------------------------------------------- */

/** Style a header row with navy locked columns and blue editable columns. */
export function xlsxStyleDualHeaderRow(
  headerRow: ExcelJS.Row,
  lockedColumnCount: number,
  height = 22
): void {
  headerRow.height = height
  headerRow.eachCell((cell, colNum) => {
    const isLocked = colNum <= lockedColumnCount
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 }
    cell.fill = isLocked ? XLSX_HEADER_FILL : XLSX_EDITABLE_HEADER_FILL
    cell.alignment = { vertical: 'middle', horizontal: 'left' }
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'FFAAAAAA' } },
    }
  })
}

/**
 * Zebra-strip a data row; locked prefix columns use grey tones, field columns white/blue tint.
 * Optional callback runs for each editable field cell (e.g. set date text numFmt).
 */
export function xlsxStyleDualZebraRow(
  dataRow: ExcelJS.Row,
  lockedColumnCount: number,
  editableFieldCount: number,
  opts?: {
    height?: number
    onFieldCell?: (cell: ExcelJS.Cell, fieldIndex: number) => void
  }
): void {
  const isEven = dataRow.number % 2 === 0
  dataRow.height = opts?.height ?? 18
  dataRow.eachCell({ includeEmpty: true }, (cell, colNum) => {
    const isLocked = colNum <= lockedColumnCount
    const fieldIndex = colNum - lockedColumnCount - 1
    if (
      !isLocked &&
      fieldIndex >= 0 &&
      fieldIndex < editableFieldCount &&
      opts?.onFieldCell
    ) {
      opts.onFieldCell(cell, fieldIndex)
    }
    const palette = isLocked ? LOCKED_DATA_FILL : EDITABLE_DATA_FILL
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: isEven ? palette.even : palette.odd },
    }
    cell.alignment = { vertical: 'middle' }
    cell.border = {
      bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } },
    }
  })
}

/** Freeze locked columns and rows above the data table header. */
export function xlsxFreezePane(
  worksheet: ExcelJS.Worksheet,
  lockedColumnCount: number,
  headerRowIndex: number
): void {
  const topCol = worksheet.getColumn(lockedColumnCount + 1).letter
  const topLeftCell = `${topCol}${headerRowIndex + 1}`
  worksheet.views = [
    {
      state: 'frozen',
      xSplit: lockedColumnCount,
      ySplit: headerRowIndex,
      topLeftCell,
      activeCell: topLeftCell,
    },
  ]
}

/* -------------------------------------------------------------------------- */
/*                           Data validation helpers                          */
/* -------------------------------------------------------------------------- */

export interface XlsxListValidationTarget {
  dataColumn: ExcelJS.Column
  options: string[]
}

/** List validation via a hidden Options sheet (Excel dropdown pattern). */
export function xlsxApplyListValidationFromOptionsSheet(
  workbook: ExcelJS.Workbook,
  dataWorksheet: ExcelJS.Worksheet,
  targets: XlsxListValidationTarget[],
  firstDataRow: number,
  lastDataRow: number,
  optionsSheetName = 'Options'
): void {
  if (targets.length === 0) return

  const optionsSheet = workbook.addWorksheet(optionsSheetName)
  optionsSheet.state = 'veryHidden'

  targets.forEach((target, optColIndex) => {
    target.options.forEach((opt, rowIndex) => {
      optionsSheet.getCell(rowIndex + 1, optColIndex + 1).value = opt
    })
    const optCol = optionsSheet.getColumn(optColIndex + 1)
    const formulae = [
      `${optionsSheetName}!$${optCol.letter}$1:$${optCol.letter}$${target.options.length}`,
    ]
    const letter = target.dataColumn.letter
    for (let r = firstDataRow; r <= lastDataRow; r++) {
      dataWorksheet.getCell(`${letter}${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae,
        showErrorMessage: true,
        errorTitle: 'Invalid value',
        error:
          target.options.length <= 10
            ? `Allowed: ${target.options.join(', ')}`
            : 'Please select a value from the dropdown.',
      }
    }
  })
}

/** YYYY-MM-DD text validation on a column (prevents Excel date serial conversion). */
export function xlsxApplyDateTextValidation(
  worksheet: ExcelJS.Worksheet,
  column: ExcelJS.Column,
  firstDataRow: number,
  lastDataRow: number,
  opts?: {
    allowPlaceholder?: string
    errorTitle?: string
    errorMessage?: string
  }
): void {
  const errorTitle = opts?.errorTitle ?? 'Invalid date format'
  const errorMessage =
    opts?.errorMessage ??
    'Enter YYYY-MM-DD as text (e.g., 2026-12-31), "-" to leave unchanged, or clear the cell to empty it.'

  column.numFmt = EXCEL_TEXT_NUMFMT
  for (let r = firstDataRow; r <= lastDataRow; r++) {
    const address = `${column.letter}${r}`
    const cell = worksheet.getCell(address)
    cell.numFmt = EXCEL_TEXT_NUMFMT
    cell.dataValidation = {
      type: 'custom',
      formulae: [
        `=${excelDateTextValidationFormula(address, opts?.allowPlaceholder)}`,
      ],
      allowBlank: true,
      showErrorMessage: true,
      errorTitle,
      error: errorMessage,
    }
  }
}

/* -------------------------------------------------------------------------- */
/*                              Download trigger                              */
/* -------------------------------------------------------------------------- */

function toArrayBufferPart(buffer: ArrayBuffer | Uint8Array): ArrayBuffer {
  if (buffer instanceof ArrayBuffer) return buffer
  return buffer.buffer.slice(
    buffer.byteOffset,
    buffer.byteOffset + buffer.byteLength
  ) as ArrayBuffer
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

/** e.g. `applications-batch-update-2026-06-02_143022.xlsx` */
export function buildXlsxDownloadFilename(basename: string): string {
  return `${basename}-${dayjs().format('YYYY-MM-DD_HHmmss')}.xlsx`
}

/**
 * Trigger a browser download from an in-memory XLSX buffer.
 * Use a full filename including `.xlsx` when the name must be fixed.
 */
export function downloadXlsxBuffer(
  buffer: ArrayBuffer | Uint8Array,
  filename: string
): void {
  triggerBlobDownload(
    new Blob([toArrayBufferPart(buffer)], { type: XLSX_MIME }),
    filename
  )
}

/** Same timestamped naming as {@link downloadXlsx}. */
export function downloadXlsxBufferWithBasename(
  buffer: ArrayBuffer | Uint8Array,
  basename: string
): void {
  downloadXlsxBuffer(buffer, buildXlsxDownloadFilename(basename))
}

/**
 * Serialize an ExcelJS workbook to a Blob and trigger a browser download.
 * The filename is suffixed with the current local timestamp.
 *
 * @param workbook  - Fully populated ExcelJS workbook
 * @param basename  - Filename without extension (e.g. `"applications"`)
 */
export async function downloadXlsx(
  workbook: ExcelJS.Workbook,
  basename: string
): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer()
  const part =
    buffer instanceof Uint8Array
      ? toArrayBufferPart(buffer)
      : (buffer as ArrayBuffer)
  triggerBlobDownload(
    new Blob([part], { type: XLSX_MIME }),
    buildXlsxDownloadFilename(basename)
  )
}
